<template>
  <div class="bg-white border border-gray-200 rounded-lg mt-4">
    <BoostRewardsControls
      @update:date-range="onDateRangeChange"
      @update:clear-filters="onClearFilters"
      @update:search-term="onSearchTermChange"
    />

    <DataTable
      :is-loading="isLoading"
      :columns="boostRewardsTableColumns"
      :data="boostRewards"
      :total-count="totalCount"
      :page-number="pageNumber"
      @update:page="onUpdatePage"
      @update:page-size="onUpdatePageSize"
    />
  </div>
</template>

<script setup lang="ts">
import DataTable from '@/components/merchant/DataTable.vue';
import { NetworkError } from '@/errors';
import type { OrderServiceGetManyRequest } from '@/models/orders';
import { fetchOrders } from '@/services/orders';
import { useGeneralStore } from '@/stores/general';
import { displayDate } from '@/utils/format.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { deepEqual } from '@/utils/paging.utils';
import type { DateRange } from 'radix-vue';
import { onMounted, ref } from 'vue';
import type { ModelOrderResponse } from '@layup/api-sdk';
import BoostRewardsControls from '../boost-rewards-controls/BoostRewardsControls.vue';
import { boostRewardsTableColumns } from './BoostRewardsTableColumns';

const generalStore = useGeneralStore();

const isLoading = ref(true);
const orderParams = ref<OrderServiceGetManyRequest>({
  state: 'COMPLETED',
  populate: ['voucher'],
  type: 'REWARDS',
});
const previousParams = ref<OrderServiceGetManyRequest>({ ...orderParams.value });
const boostRewards = ref<any[]>([]);

const totalCount = ref(1);
const pageNumber = ref(1);

onMounted(async () => onUpdateFilter(orderParams.value, true));

const onUpdateFilter = async (params: OrderServiceGetManyRequest, skipCheck: boolean = false) => {
  if (!skipCheck) {
    if (deepEqual(params, previousParams.value)) {
      return;
    }
    previousParams.value = { ...params };
  }

  boostRewards.value = [];
  isLoading.value = true;

  try {
    params.limit = generalStore.pageSize;
    params.skip = (pageNumber.value - 1) * generalStore.pageSize;
    const response = await fetchOrders(params);

    if (Object.keys(response).length > 0) {
      response.orders!.forEach((order: ModelOrderResponse) => {
        const reducedOrder = {
          id: order._id?.toString(),
          orderReference: order.reference,
          completedAt: displayDate(order.updatedAt),
          voucherCode: order.voucher?.last4Digits,
          voucherTotal: order.voucher?.initialValue,
        };
        boostRewards.value.push(reducedOrder);
      });
    }
    totalCount.value = response.totalCount || 0;
    addMetric('boost-rewards-table', { status: 'success', channel: 'merchant' });
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('boost-rewards-table', { status: 'network_error', channel: 'merchant' });
    } else {
      createException(error);
      addMetric('boost-rewards-table', { status: 'error', channel: 'merchant' });
    }
  } finally {
    isLoading.value = false;
  }
};

const onDateRangeChange = (value: DateRange): void => {
  orderParams.value.from = `${value.start?.year}-${value.start?.month}-${value.start?.day}`;
  orderParams.value.to = `${value.end?.year}-${value.end?.month}-${value.end?.day}`;
  onUpdateFilter(orderParams.value);
};

const onClearFilters = (): void => {
  orderParams.value = {
    limit: 100,
    state: 'COMPLETED',
    type: 'REWARDS',
  };
  onUpdateFilter(orderParams.value);
};

const onSearchTermChange = (value: string): void => {
  orderParams.value.search = value;
  onUpdateFilter(orderParams.value);
};

const onUpdatePage = (pageNo: number) => {
  pageNumber.value = pageNo;
  orderParams.value.skip = (pageNumber.value - 1) * generalStore.pageSize;
  onUpdateFilter(orderParams.value);
};

const onUpdatePageSize = (size: number) => {
  generalStore.pageSize = size;
  orderParams.value.limit = size;
  onUpdateFilter(orderParams.value);
};
</script>
